/**
 * Composable for create-mode line drag insertion on drawn tracks.
 *
 * When the user grabs the drawn line and drags it, this:
 * 1. Identifies which drawn segment was hit
 * 2. Disables map panning
 * 3. Tracks the drag position with a visual marker
 * 4. On release: delegates to the shared routing session to insert a route point
 *    and reroute the affected interval, matching edit mode behavior.
 *
 * Works on both desktop (mouse) and mobile (touch).
 */
import maplibregl from 'maplibre-gl'
import { computed, onUnmounted, ref, watch, type Ref, type ShallowRef } from 'vue'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import { abortPendingRouting } from '@/services/routingService'
import { showError } from '@/services/toastService'
import { useUiStore } from '@/stores/uiStore'
import type { Segment } from '@/types/tour'

const TOUCH_TARGET_LAYER_ID = 'drawing-track-touch-target'

interface UseTrackDragOptions {
  mapInstance: ShallowRef<maplibregl.Map | null> | Ref<maplibregl.Map | null>
  drawingTrackSegments: Ref<Segment[]>
  drawingTrackPoints: Ref<[number, number][]>
  isCalculatingRoute: Ref<boolean>
  onInsert?: (trackInsertIndex: number, lat: number, lon: number) => Promise<void> | void
}

/**
 * Compute the cumulative point-count boundaries for each segment,
 * so we can map a flat drawingTrackPoints index back to a segment index.
 */
function buildSegmentBoundaries(segments: Segment[]): number[] {
  const boundaries: number[] = []
  let count = 0
  for (const segment of segments) {
    count += segment.points.length
    boundaries.push(count)
  }
  return boundaries
}

/**
 * Find the segment index that "owns" a given point on the drawn line,
 * using proximity to the midpoint of each segment's coordinate range.
 */
function findClosestSegmentIndex(
  lngLat: maplibregl.LngLat,
  segments: Segment[],
  drawingPoints: [number, number][],
): number {
  if (segments.length <= 1) return 0

  const boundaries = buildSegmentBoundaries(segments)
  let bestIdx = 0
  let bestDist = Infinity

  let start = 0
  for (let i = 0; i < segments.length; i++) {
    const end = boundaries[i]
    // Check minimum distance to any point in this segment's range
    for (let j = start; j < end && j < drawingPoints.length; j++) {
      const [lon, lat] = drawingPoints[j]
      const dx = lon - lngLat.lng
      const dy = lat - lngLat.lat
      const dist = dx * dx + dy * dy
      if (dist < bestDist) {
        bestDist = dist
        bestIdx = i
      }
    }
    start = end
  }

  return bestIdx
}

function isNearDrawingRoutePoint(
  point: maplibregl.Point,
  map: maplibregl.Map,
  drawingPoints: [number, number][],
  anchorIndices: number[],
): boolean {
  const MARKER_HIT_RADIUS_PX = 24
  const hitRadiusSquared = MARKER_HIT_RADIUS_PX * MARKER_HIT_RADIUS_PX

  for (const anchorIndex of anchorIndices) {
    const coords = drawingPoints[anchorIndex]
    if (!coords) continue

    const anchorPoint = map.project(coords)
    const dx = anchorPoint.x - point.x
    const dy = anchorPoint.y - point.y
    if (dx * dx + dy * dy <= hitRadiusSquared) {
      return true
    }
  }

  return false
}

export function useTrackDrag(options: UseTrackDragOptions) {
  const { mapInstance, drawingTrackSegments, drawingTrackPoints, isCalculatingRoute } = options
  const uiStore = useUiStore()

  const isDragging = ref(false)
  const isRerouting = ref(false)
  const dragMarker = ref<maplibregl.Marker | null>(null)
  const dragSegmentIndex = ref(-1)
  let lastDragEndTime = 0
  let pendingWindowEndCancelId: number | null = null

  // Anchor indices: the cumulative indices of the first point of each segment
  // These mark where the user originally clicked
  const anchorIndices = computed<number[]>(() => {
    if (drawingTrackSegments.value.length === 0) return []
    const indices: number[] = [0] // First point is always an anchor
    let cumulative = 0
    for (let i = 0; i < drawingTrackSegments.value.length; i++) {
      cumulative += drawingTrackSegments.value[i].points.length
      if (i < drawingTrackSegments.value.length - 1) {
        indices.push(cumulative)
      } else {
        // Last point of the last segment is also an anchor
        if (cumulative > 0) indices.push(cumulative - 1)
      }
    }
    return indices
  })

  let cleanupFns: (() => void)[] = []

  const clearPendingWindowEndCancel = () => {
    if (pendingWindowEndCancelId !== null) {
      clearTimeout(pendingWindowEndCancelId)
      pendingWindowEndCancelId = null
    }
  }

  const createDragMarker = (lngLat: maplibregl.LngLat, map: maplibregl.Map) => {
    removeDragMarker()
    const el = document.createElement('div')
    el.style.width = '16px'
    el.style.height = '16px'
    el.style.borderRadius = '50%'
    el.style.backgroundColor = '#3b82f6'
    el.style.border = '3px solid #ffffff'
    el.style.boxShadow = '0 0 6px rgba(0,0,0,0.4)'
    el.style.cursor = 'grabbing'
    el.style.pointerEvents = 'none'

    const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat(lngLat)
      .addTo(map)
    dragMarker.value = marker
    return marker
  }

  const removeDragMarker = () => {
    if (dragMarker.value) {
      dragMarker.value.remove()
      dragMarker.value = null
    }
  }

  const handleDragStart = (
    lngLat: maplibregl.LngLat,
    map: maplibregl.Map,
    originalEvent: MouseEvent | TouchEvent,
    point?: maplibregl.Point,
  ) => {
    if (!uiStore.isTrackDrawingActive || isCalculatingRoute.value || isRerouting.value) return
    if (drawingTrackPoints.value.length < 2) return

    const hitPoint = point ?? map.project([lngLat.lng, lngLat.lat])
    if (isNearDrawingRoutePoint(hitPoint, map, drawingTrackPoints.value, anchorIndices.value)) {
      return
    }

    originalEvent.preventDefault()
    originalEvent.stopPropagation()

    const segIdx = findClosestSegmentIndex(
      lngLat,
      drawingTrackSegments.value,
      drawingTrackPoints.value,
    )
    dragSegmentIndex.value = segIdx
    isDragging.value = true

    map.dragPan.disable()
    createDragMarker(lngLat, map)

    map.getCanvas().style.cursor = 'grabbing'
  }

  const handleDragMove = (lngLat: maplibregl.LngLat) => {
    if (!isDragging.value || !dragMarker.value) return
    dragMarker.value.setLngLat(lngLat)
  }

  const handleDragEnd = async (lngLat: maplibregl.LngLat, map: maplibregl.Map) => {
    if (!isDragging.value) return

    clearPendingWindowEndCancel()
    isDragging.value = false
    lastDragEndTime = Date.now()
    map.dragPan.enable()
    map.getCanvas().style.cursor = ''
    removeDragMarker()

    const segIdx = dragSegmentIndex.value
    if (segIdx < 0 || segIdx >= drawingTrackSegments.value.length) return
    const maxInsertIndex = Math.max(1, drawingTrackSegments.value.length - 1)
    const trackInsertIndex = Math.min(Math.max(segIdx, 1), maxInsertIndex)

    isRerouting.value = true
    isCalculatingRoute.value = true

    try {
      if (!options.onInsert) {
        showError('Track drag is not available right now')
        return
      }

      await options.onInsert(trackInsertIndex, lngLat.lat, lngLat.lng)
    } catch (error) {
      logService.error(LOG_CATEGORIES.MAP, 'Drag reroute failed', { error })
    } finally {
      isRerouting.value = false
      isCalculatingRoute.value = false
      dragSegmentIndex.value = -1
    }
  }

  const setupDragHandlers = (map: maplibregl.Map) => {
    cleanup()

    // Mouse events on the touch target layer
    const onMouseDown = (e: maplibregl.MapLayerMouseEvent) => {
      handleDragStart(e.lngLat, map, e.originalEvent, e.point)
    }
    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      handleDragMove(e.lngLat)
    }
    const onMouseUp = (e: maplibregl.MapMouseEvent) => {
      clearPendingWindowEndCancel()
      void handleDragEnd(e.lngLat, map)
    }

    // Touch events on the touch target layer
    const onTouchStart = (e: maplibregl.MapLayerTouchEvent) => {
      if (e.originalEvent.touches.length !== 1) return
      handleDragStart(e.lngLat, map, e.originalEvent, e.point)
    }
    const onTouchMove = (e: maplibregl.MapTouchEvent) => {
      if (!isDragging.value) return
      handleDragMove(e.lngLat)
    }
    const onTouchEnd = (e: maplibregl.MapTouchEvent) => {
      if (!isDragging.value) return
      clearPendingWindowEndCancel()
      void handleDragEnd(e.lngLat, map)
    }
    const onWindowMouseUp = () => {
      cancelDrag()
    }
    const onWindowTouchEnd = () => {
      cancelDrag()
    }
    const onWindowBlur = () => {
      cancelDrag()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        cancelDrag()
      }
    }

    // Cursor hint: show grab cursor when hovering the drawn line
    const onLayerMouseEnter = () => {
      if (
        !isDragging.value &&
        uiStore.isTrackDrawingActive &&
        drawingTrackSegments.value.length > 0
      ) {
        map.getCanvas().style.cursor = 'grab'
      }
    }
    const onLayerMouseLeave = () => {
      if (!isDragging.value) {
        map.getCanvas().style.cursor = ''
      }
    }

    // Layer-specific events (only fire when over the touch target)
    map.on('mousedown', TOUCH_TARGET_LAYER_ID, onMouseDown)
    map.on('touchstart', TOUCH_TARGET_LAYER_ID, onTouchStart)
    map.on('mouseenter', TOUCH_TARGET_LAYER_ID, onLayerMouseEnter)
    map.on('mouseleave', TOUCH_TARGET_LAYER_ID, onLayerMouseLeave)

    // Global events for tracking drag movement (fire everywhere on map)
    map.on('mousemove', onMouseMove)
    map.on('mouseup', onMouseUp)
    map.on('touchmove', onTouchMove)
    map.on('touchend', onTouchEnd)
    window.addEventListener('mouseup', onWindowMouseUp)
    window.addEventListener('touchend', onWindowTouchEnd)
    window.addEventListener('touchcancel', onWindowTouchEnd)
    window.addEventListener('blur', onWindowBlur)
    document.addEventListener('visibilitychange', onVisibilityChange)

    cleanupFns = [
      () => map.off('mousedown', TOUCH_TARGET_LAYER_ID, onMouseDown),
      () => map.off('touchstart', TOUCH_TARGET_LAYER_ID, onTouchStart),
      () => map.off('mouseenter', TOUCH_TARGET_LAYER_ID, onLayerMouseEnter),
      () => map.off('mouseleave', TOUCH_TARGET_LAYER_ID, onLayerMouseLeave),
      () => map.off('mousemove', onMouseMove),
      () => map.off('mouseup', onMouseUp),
      () => map.off('touchmove', onTouchMove),
      () => map.off('touchend', onTouchEnd),
      () => window.removeEventListener('mouseup', onWindowMouseUp),
      () => window.removeEventListener('touchend', onWindowTouchEnd),
      () => window.removeEventListener('touchcancel', onWindowTouchEnd),
      () => window.removeEventListener('blur', onWindowBlur),
      () => document.removeEventListener('visibilitychange', onVisibilityChange),
    ]
  }

  const cleanup = () => {
    clearPendingWindowEndCancel()
    removeDragMarker()
    cleanupFns.forEach((fn) => fn())
    cleanupFns = []
    isDragging.value = false
    isRerouting.value = false
  }

  const cancelDrag = () => {
    // Only cancel a drag that is still in progress. Once the pointer is released,
    // `handleDragEnd` sets `isDragging = false` and commits the reroute; the drop's
    // own `window` mouseup/touchend (and any blur/visibility change during the
    // in-flight reroute) must NOT abort that routing — otherwise the BRouter
    // recalculation of the changed sections is killed and the insert degrades to a
    // straight-line fallback. Mirrors useTrackEditInsertDrag.cancelDrag.
    if (!isDragging.value) return

    clearPendingWindowEndCancel()
    abortPendingRouting()
    isDragging.value = false
    isRerouting.value = false
    isCalculatingRoute.value = false
    dragSegmentIndex.value = -1
    lastDragEndTime = Date.now()
    removeDragMarker()

    const map = mapInstance.value
    if (map) {
      map.dragPan.enable()
      map.getCanvas().style.cursor = ''
    }
  }

  // Auto-setup when map instance and drawing mode are both active
  watch(
    [mapInstance, () => uiStore.isTrackDrawingActive],
    ([map, isDrawing]) => {
      if (map && isDrawing) {
        // Check layer exists before setting up — it may not be added yet
        const trySetup = () => {
          if (map.getLayer(TOUCH_TARGET_LAYER_ID)) {
            setupDragHandlers(map)
          }
        }
        // Try immediately and also listen for layer additions via sourcedata
        trySetup()
        if (!map.getLayer(TOUCH_TARGET_LAYER_ID)) {
          const onSourceData = () => {
            if (map.getLayer(TOUCH_TARGET_LAYER_ID)) {
              map.off('sourcedata', onSourceData)
              setupDragHandlers(map)
            }
          }
          map.on('sourcedata', onSourceData)
          cleanupFns.push(() => map.off('sourcedata', onSourceData))
        }
      } else {
        cleanup()
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    cleanup()
  })

  /** Returns true if a drag just ended (within 300ms) — use to suppress map click. */
  const shouldSuppressClick = () => Date.now() - lastDragEndTime < 300

  return {
    isDragging,
    isRerouting,
    anchorIndices,
    cancelDrag,
    shouldSuppressClick,
  }
}
